import React, { useEffect, useState } from 'react';
import { BadgePercent } from 'lucide-react';
import * as offersApi from '../api/endpoints/offers';
import { useAuth } from '../context/AuthContext';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { isStaleLocationError } from '../utils/staleLocation';
import { formatOfferDate, offerBenefitText, offerConditionTexts } from '../utils/offerPresentation';
import { SkeletonBlock } from './Skeleton';
import { homeShell } from '../styles/ui';

// Promotions currently offered on the website at the outlet serving the
// browsing location — for discovery only. GET /offers already returns only
// this storefront's offers for this location (and this customer, when signed
// in); nothing here filters, ranks or decides whether an offer applies to a
// product or a cart. Those answers come from the product projections and the
// cart quote.
//
// Optional: while loading it shows a light skeleton, and when there are no
// offers or the request fails the section simply is not there.
const OfferCard = ({ offer }) => {
  const title = offer.label || offer.name;
  const benefit = offerBenefitText(offer);
  const conditions = offerConditionTexts(offer);
  const endsOn = formatOfferDate(offer.endsAt);
  const code = offer.entry === 'code' ? offer.code : null;

  return (
    <li
      className="shrink-0 w-[16.5rem] sm:w-auto snap-start flex flex-col rounded-2xl border border-accent-100 bg-gradient-to-br from-accent-50 to-white p-4 shadow-soft"
      data-testid="website-offer"
    >
      <div className="flex items-start gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-white text-accent-600 border border-accent-100 flex items-center justify-center shrink-0">
          <BadgePercent size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-emerald-950 leading-snug line-clamp-2">{title}</h3>
          {benefit && <p className="text-sm font-semibold text-accent-700 mt-0.5">{benefit}</p>}
        </div>
      </div>
      {offer.description && <p className="text-sm text-ink-secondary mt-2 line-clamp-2">{offer.description}</p>}
      {conditions.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-ink-secondary">
          {conditions.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}
      <div className="mt-auto pt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        {code ? (
          <span className="font-semibold text-emerald-900">
            Code <span className="font-mono tracking-wide bg-white border border-dashed border-emerald-300 rounded-md px-1.5 py-0.5">{code}</span>
          </span>
        ) : (
          <span className="font-medium text-emerald-700">Applied automatically</span>
        )}
        {endsOn && <span className="text-ink-secondary">Valid till {endsOn}</span>}
      </div>
    </li>
  );
};

const WebsiteOffers = () => {
  const { authStatus } = useAuth();
  const { storefrontStatus, locationVersion } = useDeliveryLocation();
  const ready = storefrontStatus === 'ready' && authStatus !== 'loading';
  const [state, setState] = useState({ status: 'loading', offers: [] });

  // One request per effective location (and sign-in state, since some offers
  // are for particular customers). A label-only location change keeps the
  // same locationVersion and so the same offers; an answer for a previous
  // location is dropped here and rejected by the client.
  useEffect(() => {
    if (!ready) {
      setState({ status: 'loading', offers: [] });
      return undefined;
    }
    let active = true;
    setState({ status: 'loading', offers: [] });
    offersApi
      .listOffers()
      .then((response) => {
        if (!active) return;
        const offers = Array.isArray(response?.data?.offers) ? response.data.offers : [];
        setState({ status: 'ready', offers });
      })
      .catch((error) => {
        if (!active || isStaleLocationError(error)) return;
        setState({ status: 'error', offers: [] });
      });
    return () => {
      active = false;
    };
  }, [ready, locationVersion, authStatus]);

  if (!ready || state.status === 'error' || (state.status === 'ready' && state.offers.length === 0)) return null;

  return (
    <section id="offers" aria-labelledby="website-offers-heading" className="pt-1 lg:pt-3 pb-5 bg-white">
      <div className={homeShell}>
        <h2 id="website-offers-heading" className="serif text-2xl sm:text-3xl xl:text-4xl text-emerald-950 mb-4">
          Offers for You
        </h2>
        {state.status === 'loading' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="website-offers-loading">
            <SkeletonBlock className="h-32 rounded-2xl" />
            <SkeletonBlock className="hidden sm:block h-32 rounded-2xl" />
            <SkeletonBlock className="hidden lg:block h-32 rounded-2xl" />
          </div>
        ) : (
          <ul className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto sm:overflow-visible snap-x -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            {state.offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default WebsiteOffers;
